import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, KeyRound, Delete } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import CaptchaField from "./CaptchaField";
import SecurityAlert from "./SecurityAlert";

const keyCls = "h-14 rounded-xl border border-slate-200 bg-white text-lg font-medium text-[#0B1220] hover:border-[#5698C7] hover:text-[#5698C7] active:scale-95 transition-all duration-150 shadow-sm";

export default function PinModal({ onClose }) {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [alerted, setAlerted] = useState(false);
  const captchaRef = useRef(null);

  const verify = async (value) => {
    if (!captchaToken) {
      setError("Completa la verificación reCAPTCHA"); setPin(""); return;
    }
    setChecking(true); setError("");
    try {
      const { data } = await api.post("/auth/pin", { pin: value, recaptcha_token: captchaToken });
      sessionStorage.setItem("nc_pin_token", data.pin_token);
      onClose();
      navigate("/login");
    } catch (e) {
      if (e.response?.status === 429) setAlerted(true);
      else setError(formatApiErrorDetail(e.response?.data?.detail) || "PIN incorrecto");
      captchaRef.current?.reset();
      if (captchaToken !== "disabled") setCaptchaToken("");
      setPin("");
    } finally { setChecking(false); }
  };

  const press = (d) => {
    if (checking) return;
    setError("");
    const next = (pin + d).slice(0, 6);
    setPin(next);
    if (next.length === 6) verify(next);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pin-modal">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-[#5698C7] text-white flex items-center justify-center"><KeyRound size={16} /></span>
            <div>
              <h3 className="font-display text-sm font-bold text-[#0B1220]" data-testid="pin-title">User PIN</h3>
              <p className="text-[11px] text-slate-500">Need to add the Pin Before validate your credentials</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="pin-close"><X size={16} className="text-slate-400" /></button>
        </div>

        <div className="flex justify-center gap-3 mb-6" data-testid="pin-dots">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className={`w-3.5 h-3.5 rounded-full transition-colors duration-150 ${i < pin.length ? "bg-[#5698C7]" : "bg-slate-200"}`} />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <button key={d} onClick={() => press(String(d))} className={keyCls} data-testid={`pin-key-${d}`}>{d}</button>
          ))}
          <button onClick={() => { setPin(""); setError(""); }} className="h-14 text-xs text-slate-500 hover:text-[#0B1220] transition-colors duration-150" data-testid="pin-clear">Borrar</button>
          <button onClick={() => press("0")} className={keyCls} data-testid="pin-key-0">0</button>
          <button onClick={() => setPin(pin.slice(0, -1))} className="h-14 flex items-center justify-center text-slate-500 hover:text-[#0B1220] transition-colors duration-150" data-testid="pin-backspace"><Delete size={18} /></button>
        </div>

        {alerted && <div className="mb-4 max-h-56 overflow-y-auto"><SecurityAlert /></div>}
        {error && <p className="text-xs text-red-600 text-center mb-3" data-testid="pin-error">{error}</p>}
        {checking && <p className="text-xs text-slate-400 text-center mb-3">Verificando…</p>}

        <div className="flex justify-center mb-4"><CaptchaField captchaRef={captchaRef} onToken={setCaptchaToken} /></div>

        <div className="flex justify-start">
          <button onClick={onClose} className="text-xs text-slate-500 hover:text-[#0B1220] transition-colors duration-150" data-testid="pin-cancel">Cancelar</button>
        </div>
      </div>
    </div>
  );
}
